import React, { useState } from "react";
import axios from "axios";
import _ from "lodash";
import Alert from "components/atoms/Alert";
import {
  setAuthRes,
  setUserLoggedIn,
  logoutHandler,
} from "redux/auth/auth.action";
import { removeValues, setValues } from "services/storage";
import { BASE_URL, getRequest, postRequest } from "../services/http";
import { getValues } from "../services/storage";
import { store } from "../redux/store";
import { StatusType } from "types";

const state = store.getState();
const bearerToken = state.auth.token;

export const inputNum = /^[0-9]*$/;
export const inputFloat = /^\d*\.?(?:\d{1,2})?$/;
export const inputAlpha = /^[a-zA-Z\s]+$/;
export const validteEmail = /\S+@\S+\.\S+/;
export const inputEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const specialChar = /(?=.*?[#?!@$%^&*-])/;
export const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;

export const appMode = () => {
  return "development";
};

export function toMoney(value: string | any) {
  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "NGN",
  });

  return `₦${formatter.format(value).replace("NGN", "").trim()}`;
}

export const handleException = (error: any) => {
  try {
    if (error.response) {
      if (error.response.data.message === "Invalid token") {
        Alert("info", "Token expired, reinitiate log in.");
        logoutFunc(bearerToken);
      } else {
        Alert("error", error.response.data.message);
      }
    } else {
      Alert("error", error.message);
    }
  } catch (e) {
    Alert("error", "We are unable to process your request at the moment.");
  }
};

export const commaSeparateNumber = (val: string) => {
  if (val.length > 3) {
    return (Number(val.replace(/\D/g, "")) || "").toLocaleString();
  }
  return val;
};

export const logoutFunc = async (bearerToken) => {
  try {
    const res = await axios.post(`${BASE_URL}logout?token=${bearerToken}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    if (res.data.code === "error") {
      Alert("error", "Something went wrong");
    } else {
      logoutHandler();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/auth/login";
    }
    return res;
  } catch (error) {
    handleException(error);
  }
};

export const handleLogout = () => {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = "/auth/login";
};

export const newLogoutHandler = async (bearerToken) => {
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${bearerToken}`,
  };
  const res = await postRequest(`logout?token=${bearerToken}`, {
    headers: headers,
  });

  if (res?.data.responseCode === "00") {
    logoutHandler();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/auth/login";
  } else {
    Alert("error", "Something went wrong");
    return res;
  }
};

export const getBusinessInitials = (name): string => {
  const bnl: string[] = name.split(" ");
  let initials = bnl[0].charAt(0);
  if (bnl.length === 1) {
    initials = initials + bnl[0].charAt(1) + bnl[0].charAt(2);
  } else if (bnl.length > 1) {
    initials = initials + bnl[1].charAt(0) + bnl[1].charAt(1);
  }
  return initials.toLocaleUpperCase();
};

export function generateRandomAlphanumeric(n: number) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < n; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }

  return result;
}

export const getStatus = (code: string): StatusType => {
  switch (code) {
    case "00":
      return "SUCCESSFUL";
    case "S20":
      return "PENDING";
    default:
      return "FAILED";
  }
};

export const getFileType = (fileName) => {
  return fileName.split(".").pop();
};

const signatures = {
  JVBERi0: "application/pdf",
  R0lGODdh: "image/gif",
  R0lGODlh: "image/gif",
  iVBORw0KGgo: "image/png",
  "/9j/": "image/jpg",
};

export const detectMimeType = (b64) => {
  for (var s in signatures) {
    if (b64.indexOf(s) !== -1) {
      return signatures[s];
    }
  }
};

export function base64toBlob(base64Data) {
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: "application/pdf" });
}

export const getKycDocType = (doc) => {
  let type: any = null;
  try {
    if (doc) {
      if (
        doc.kycRecordUpdate &&
        typeof doc.kycRecordUpdate === "string" &&
        doc.kycRecordUpdate.length
      ) {
        type = "TEXT";
      } else if (
        doc.kycDocumentUrl &&
        typeof doc.kycDocumentUrl === "string" &&
        doc.kycDocumentUrl.length
      ) {
        try {
          const linkSplit = doc.kycDocumentUrl.split(".");
          const linkType = linkSplit[linkSplit.length - 1];
          if (
            linkType === "jpeg" ||
            linkType === "jpg" ||
            linkType === "png" ||
            linkType === "gif"
          ) {
            type = "IMAGE";
          } else if (linkType === "pdf") {
            type = "PDF";
          }
        } catch (e) {}
      }
    }
    return type;
  } catch (e) {}
};
